import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import { trackV2 } from 'utils/analytics';

import styles from './index.scss';
import PromoCodeInput from './PromoCodeInput';

import { postCheckPromoCode } from '../../../actions';

export const I18nPrefix = 'strava.subscriptions.checkout.summary.organic';

function renderPerMonthPrice(price, currency) {
  return I18n.t_html(
    `${I18nPrefix}.per_month`,
    {
      price: Cldr.formatCurrency(price, currency)
    },
    {
      tagName: 'div'
    }
  );
}

function renderPerYearPrice(price, currency) {
  return I18n.t_html(
    `${I18nPrefix}.per_year`,
    {
      price: Cldr.formatCurrency(price, currency)
    },
    {
      tagName: 'div'
    }
  );
}

export class PricingList extends React.Component {
  handlePostCheckPromoCode = (code) => {
    const {
      packageIds,
      handlePostCheckPromoCode,
      isTrial,
      origin
    } = this.props;

    trackV2({
      category: 'checkout',
      page: 'cart',
      action: 'click',
      element: 'promo_code_submit',
      properties: {
        origin,
        subscription_type: isTrial ? 'trial' : 'organic'
      }
    });

    const formData = {
      code,
      package_ids: packageIds
    };
    handlePostCheckPromoCode(formData);
  };

  discountItemText = (discount) =>
    `${I18n.t(discount.nameKey)} (-${Math.round(discount.percent)}%)`;

  isYearlyDuration = () => {
    const { cartData } = this.props;
    return cartData.duration === 'yearly';
  };

  totalPrimaryPrice = () => {
    const { cartData, isTrial } = this.props;

    if (isTrial) {
      return this.isYearlyDuration()
        ? cartData.yearlyTotal
        : cartData.monthlyTotal;
    }

    return cartData.billedToday;
  };

  totalSecondaryPrice = () => {
    const { cartData, isTrial } = this.props;

    if (isTrial) {
      return this.isYearlyDuration()
        ? cartData.monthlyTotal
        : cartData.yearlyTotal;
    }

    return cartData.billedTodayOther;
  };

  render() {
    const {
      checkoutSubmitting,
      promoCodeSubmitting,
      cartData,
      promotionApplied,
      isPromoCodeInputVisable,
      hasPromoCodeError,
      showTotals,
      isTrial
    } = this.props;
    const { currency } = cartData;

    const shouldShowPromoInput = !promotionApplied && isPromoCodeInputVisable;

    const subtotalText = this.isYearlyDuration()
      ? renderPerYearPrice(cartData.yearlyTotal, currency)
      : renderPerMonthPrice(cartData.monthlyTotal, currency);

    const totalPrimaryText = this.isYearlyDuration()
      ? renderPerYearPrice(this.totalPrimaryPrice(), currency)
      : renderPerMonthPrice(this.totalPrimaryPrice(), currency);

    const totalSecondaryText = this.isYearlyDuration()
      ? renderPerMonthPrice(this.totalSecondaryPrice(), currency)
      : renderPerYearPrice(this.totalSecondaryPrice(), currency);

    return (
      <div className={styles.pricingList}>
        {cartData.discounts.length > 0 && (
          <>
            <div className={styles.summaryRow}>
              <div>{I18n.t(`${I18nPrefix}.subtotal`)}</div>
              <div className={styles.priceText}>{subtotalText}</div>
            </div>
            {cartData.discounts.map((discount) => (
              <div className={styles.summaryRow} key={discount.nameKey}>
                <div>{this.discountItemText(discount)}</div>
                <div className={styles.priceText}>
                  {this.isYearlyDuration()
                    ? renderPerYearPrice(-discount.amount, currency)
                    : renderPerMonthPrice(-discount.amount, currency)}
                </div>
              </div>
            ))}
            <div className={styles.divider} />
          </>
        )}

        {shouldShowPromoInput && (
          <>
            <PromoCodeInput
              isSubmitting={promoCodeSubmitting}
              disabled={checkoutSubmitting || promoCodeSubmitting}
              hasError={hasPromoCodeError}
              handlePostCheckPromoCode={this.handlePostCheckPromoCode}
            />
            <div className={styles.divider} />
          </>
        )}

        {showTotals && (
          <>
            <div className={styles.totals}>
              <div data-test="price-list-total-text">
                {isTrial
                  ? I18n.t(`${I18nPrefix}.total_trial`)
                  : I18n.t(`${I18nPrefix}.total`)}
              </div>
              <div className="text-right">
                <div
                  className={styles.totalPrimaryText}
                  data-test="price-list-total-primary-text"
                >
                  {totalPrimaryText}
                </div>
                <div
                  className={styles.totalSecondaryText}
                  data-test="price-list-total-secondary-text"
                >
                  {totalSecondaryText}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

PricingList.defaultProps = {
  showTotals: true,
  isTrial: false
};

PricingList.propTypes = {
  packageIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  checkoutSubmitting: PropTypes.bool.isRequired,
  promoCodeSubmitting: PropTypes.bool.isRequired,
  promotionApplied: PropTypes.bool.isRequired,
  cartData: PropTypes.shape({
    billedToday: PropTypes.number.isRequired,
    billedTodayOther: PropTypes.number.isRequired,
    monthlyTotal: PropTypes.number.isRequired,
    yearlyTotal: PropTypes.number.isRequired,
    priceWithoutDiscount: PropTypes.number.isRequired,
    discounts: PropTypes.arrayOf(
      PropTypes.shape({
        nameKey: PropTypes.string.isRequired,
        percent: PropTypes.number.isRequired,
        amount: PropTypes.number.isRequired
      })
    ),
    currency: PropTypes.string.isRequired,
    duration: PropTypes.string.isRequired
  }).isRequired,
  isPromoCodeInputVisable: PropTypes.bool.isRequired,
  hasPromoCodeError: PropTypes.bool.isRequired,
  handlePostCheckPromoCode: PropTypes.func.isRequired,
  showTotals: PropTypes.bool,
  isTrial: PropTypes.bool,
  origin: PropTypes.string.isRequired
};

const mapStateToProps = (state) => ({
  duration: state.duration,
  cartData: state.products[state.duration],
  packageIds: state.package_ids,
  checkoutSubmitting: state.checkout_submitting,
  promoCodeSubmitting: state.promo_code_submitting,
  hasPromoCodeError: state.promo_code_error,
  promotionApplied: !!state.products.promotion.code,
  isTrial: state.isTrial,
  isPromoCodeInputVisable: state.promo_code_input_visibility,
  origin: state.analytics_data.origin
});

export default connect(
  mapStateToProps,
  {
    handlePostCheckPromoCode: postCheckPromoCode
  }
)(PricingList);
